import { useMemo, useState } from "react"

import { Box, Button, Grid, Skeleton, Stack, Typography } from "@mui/material"
import { useMutationDeleteSchoolGroup } from "api/reactQuery/mutations/schoolGroups"
import { useQuerySchoolGroups } from "api/reactQuery/queries/schoolGroups"
import { useQueryUserMe } from "api/reactQuery/queries/userMe"
import GroupCard from "components/common/groupCard"
import { userIsFreeDemoSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import AddGroupMenu from "pages/groups/components/AddGroupMenu"
import ImportGroupsDialog from "pages/groups/components/ImportGroupsDialog"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IGroup } from "ts/interfaces/Group"
import { getErrorMessageFromTab } from "utils/api"

import DeleteGroupsDialog from "../groups/components/DeleteGroupsDialog"
import GroupsUpgradePrompt from "../groups/components/GroupsUpgradePrompt"
import AddSchoolGroupCard from "./components/AddSchoolGroupCard"
import CreateSchoolGroupDialog from "./components/CreateSchoolGroupDialog"
import EditSchoolGroupDialog from "./components/EditSchoolGroupDialog"
import ExportSchoolGroupsMenu from "./components/ExportSchoolGroupsMenu"
import SchoolGroupsEmptyState from "./components/SchoolGroupsEmptyState"
import SendSchoolGroupTransactionDialog from "./components/SendSchoolGroupTransactionDialog"

const GRID_SIZE = { mobile: 12, tablet: 6, desktop: 3 }

type DeleteTarget = { group: IGroup }

const SchoolGroups = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const { isLoading: isUserMeLoading } = useQueryUserMe()
  const isFreeDemo = userIsFreeDemoSchoolPlan()

  const { data, isLoading: isGroupsLoading } = useQuerySchoolGroups({
    options: { enabled: !isUserMeLoading && !isFreeDemo },
  })
  const groups = useMemo(() => data?.data ?? [], [data])

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [editGroup, setEditGroup] = useState<IGroup | null>(null)
  const [transactionGroup, setTransactionGroup] = useState<IGroup | null>(null)

  const { mutate: deleteGroup, isLoading: isDeletingGroup } =
    useMutationDeleteSchoolGroup({
      options: {
        onSuccess: () => {
          showSnackbar({ title: t("group.groupDeletedSuccess") })
          setDeleteTarget(null)
        },
        onError: (error) =>
          showSnackbar({
            title: t(getErrorMessageFromTab(error)),
            variant: "error",
          }),
      },
    })

  const confirmDelete = () => {
    if (!deleteTarget) return
    deleteGroup({ groupId: deleteTarget.group.id })
  }

  if (isUserMeLoading || isGroupsLoading) {
    return (
      <Stack p="16px">
        <Grid container spacing="16px" mt="8px">
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid key={index} size={GRID_SIZE}>
              <Skeleton variant="rounded" height={220} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    )
  }

  if (isFreeDemo) return <GroupsUpgradePrompt />

  const isEmpty = groups.length === 0

  return (
    <Stack gap="16px" p="16px">
      <Typography variant="h1" sx={visuallyHiddenSx}>
        {t("adminDashboard.grouping")}
      </Typography>

      {!isEmpty && (
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-end"
          gap="8px"
        >
          <Button onClick={(e) => setAddAnchor(e.currentTarget)}>
            {t("group.addGroup")}
          </Button>
          <ExportSchoolGroupsMenu />
        </Stack>
      )}

      {isEmpty ? (
        <SchoolGroupsEmptyState
          onImportGroup={() => setIsImportOpen(true)}
          onCreateGroup={() => setIsCreateOpen(true)}
        />
      ) : (
        <Box bgcolor="mockup.primary99" p="16px" borderRadius="8px">
          <Grid container spacing="16px">
            {groups.map((group) => (
              <Grid key={group.id} size={GRID_SIZE}>
                <GroupCard
                  group={group}
                  hideCheckbox
                  onCardClick={() => setTransactionGroup(group)}
                  onEdit={() => setEditGroup(group)}
                  onDelete={() => setDeleteTarget({ group })}
                  showSendTransactionCta
                />
              </Grid>
            ))}
            <Grid size={GRID_SIZE}>
              <AddSchoolGroupCard onClick={setAddAnchor} />
            </Grid>
          </Grid>
        </Box>
      )}

      <AddGroupMenu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        onCreateNew={() => setIsCreateOpen(true)}
        onUploadImport={() => setIsImportOpen(true)}
      />

      <CreateSchoolGroupDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {isImportOpen && (
        <ImportGroupsDialog
          open
          target={{ scope: "SCHOOL" }}
          onClose={() => setIsImportOpen(false)}
        />
      )}

      {editGroup && (
        <EditSchoolGroupDialog
          open
          group={editGroup}
          onClose={() => setEditGroup(null)}
        />
      )}

      {transactionGroup && (
        <SendSchoolGroupTransactionDialog
          open
          group={transactionGroup}
          onClose={() => setTransactionGroup(null)}
        />
      )}

      {deleteTarget && (
        <DeleteGroupsDialog
          open
          groupNames={[deleteTarget.group.name]}
          isLoading={isDeletingGroup}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </Stack>
  )
}

export default SchoolGroups
